<template>
  <div class="picker-input">
    <label class="label">{{ attrs.label }}</label>
    <multiselect
      :ref="'multiselect-' + attrs.key"
      :label="attrs.displayedBy"
      :custom-label="attrs.customLabel"
      :group-values="attrs.groupValues"
      :group-label="attrs.groupLabel"
      :allowEmpty="false"
      :placeholder="placeholder"
      :internal-search="false"
      :track-by="attrs.trackBy"
      :value="computedValue"
      :options="computedOptions"
      :maxHeight="243"
      openDirection="below"
      :disabled="disabled"
      @input="(selected) => handleInput({ attrs: attrs, value: selected })"
      @search-change="handleSearchChange"
      @open="handleOpen"
      @close="handleClose"
    >
      <!-- Hide the default caret -->
      <template #caret>
        <div class="caret-wrapper">
          <div v-if="search != ''" class="mc-fa icon-fill_close-circle" @mousedown.prevent.stop="clearSearch()" />
          <i v-if="!isOpen" class="mc-fa icon-outline_chevron-down" />
        </div>
      </template>

      <template #placeholder>
        <div class="placeholder-wrapper d-flex gap-2 align-items-center">
          <span class="__placeholder">
            {{ placeholder }}
          </span>
        </div>
      </template>

      <template #selection>
        <template v-if="isOpen">
          <i class="icon-outline_search" />
        </template>
      </template>

      <!-- If there is no option -->
      <template #noResult>
        <template v-if="dataApi.length === 0 && totalCount === 0">
          {{ $t('fleetTaskInstant.text.noData') }}
        </template>
        <div v-else />
      </template>

      <!-- Observer to get next page data -->
      <template #afterList>
        <b-skeleton-wrapper :loading="isLoading && hasNextPage">
          <template #loading>
            <div class="px-2 py-2 d-flex gap-3 flex-column">
              <b-skeleton width="100" />
            </div>
          </template>
        </b-skeleton-wrapper>
        <div v-observe-visibility="handleElementObserved" style="height: 10px" />
      </template>

      <template #option="{ option }">
        <template v-if="option.$isLabel">
          <div class="__option-group">
            {{ option.$groupLabel.value }}
            <template v-if="option.$groupLabel.tooltip">
              <div
                :id="`tooltip-data-${option.$groupLabel.key}`"
                class="mc-icon"
                :class="{
                  'icon-outline_info': hoveredKey !== option.$groupLabel.key,
                  'icon-fill_info': hoveredKey === option.$groupLabel.key,
                }"
                @mouseenter.prevent.stop="hoveredKey = option.$groupLabel.key"
                @mouseleave.prevent.stop="hoveredKey = null"
              />
              <b-popover
                triggers="hover"
                placement="rightbottom"
                :target="`tooltip-data-${option.$groupLabel.key}`"
              >
                <div class="popover-text-wrapper">
                  {{ option.$groupLabel.tooltip }}
                </div>
              </b-popover>
            </template>
          </div>
        </template>
        <template v-else>
          <div class="__option-list" v-html="attrs.customValue(computedInitialValue, option)" />
        </template>
      </template>
    </multiselect>
    <div v-if="attrs.required" class="validation">
      <strong>*</strong>
      <span>{{ $t('global.validation.required') }}</span>
    </div>
  </div>
</template>

<script>
import Multiselect from 'vue-multiselect'

import { debounce } from '@/helpers/debounce'
import { isEmpty } from 'lodash'

export default {
  components: {
    Multiselect,
  },
  props: {
    attrs: {
      type: Object,
      required: true
    },
    initialValue: {
      type: Object,
      required: true,
    },
    modelValue: {
      type: Object,
      default: null,
    },
    disabled: {
      type: Boolean,
      default: false,
    }
  },
  emits: ['update:modelValue'],
  data() {
    return {
      isOpen: false,
      isLoading: false,
      isFromClear: false,
      isInitial: false,

      search: '',
      hoveredKey: null,

      page: 1,
      limit: 10,
      totalCount: null,
      isPagination: true,

      dataApi: [],

      localValue: this.modelValue
    }
  },
  computed: {
    placeholder() {
      return this.attrs.placeholder
    },
    computedInitialValue() {
      return this.initialValue ?? {}
    },
    computedValue() {
      // set value null if there is no selected
      // so the placeholder will be shown
      if (!this.localValue?.[this.attrs?.trackBy] || !this.computedInitialValue) {
        return null
      }

      const val = this.localValue ?? this.computedInitialValue

      return { ...val }
    },
    computedOptions() {
      return [
        {
          ...this.attrs?.options[0],
          data: isEmpty(this.computedInitialValue) ? [] : [this.computedInitialValue],
        },
        {
          ...this.attrs?.options[1],
          data: this.dataApi,
        }
      ]
    },
    hasNextPage() {
      if (!this.isPagination) return false

      // If totalCount is null or undefined The data has not been fetched yet
      // so we return true to get the actual total count
      if (!this.totalCount) return true

      if (this.totalCount > 0 && this.totalCount !== this.dataApi.length) return true

      return this.page * this.limit < this.totalCount
    },
  },
  watch: {
    modelValue(newValue) {
      this.localValue = newValue
    }
  },
  mounted() {
    this.search = this.computedValue?.[this.attrs?.displayedBy] || ''
  },
  methods: {
    isEmpty,
    handleOpen() {
      this.isOpen = true
      this.isInitial = true

      if (!this.isFromClear) {
        this.search = this.computedValue?.[this.attrs?.displayedBy] || ''
        
        let multiselect = this.$refs?.[`multiselect-${this.attrs.key}`]
        multiselect.updateSearch(this.search)
      }
      this.isFromClear = false
    },
    handleClose() {
      this.isOpen = false
      this.isInitial = true
      this.resetData(this.computedValue?.[this.attrs?.displayedBy] || '')
    },

    async getData(opts, type = 'initial') {
      if (this.isLoading) return
      this.isLoading = true

      if (!this.attrs.listGetter) {
        this.isLoading = false
        return
      }

      const res = await this.attrs.listGetter(opts)

      if (!res || res.errors) {
        this.isLoading = false
        return
      }

      this.dataApi = type === 'initial' ? res.data.paginated_result : [...this.dataApi, ...res.data.paginated_result]
      this.ids = res.data.ids
      this.page = res.metadata.page
      this.isPagination = res.isPagination !== undefined ? res.isPagination : true

      this.totalCount = res.metadata.total_count
      this.isLoading = false
      this.isInitial = false
    },

    async handleElementObserved(isVisible) {
      if (!isVisible) return
      if (!this.isOpen) return
      if (!this.hasNextPage) return
      if (this.isLoading) return

      await this.getData({
        limit: this.limit,
        page: this.totalCount ? ++this.page : this.page,
        search: this.search !== '' ? this.search : undefined,
      }, 'next_page')
    },

    handleSearchChange(search) {
      if (this.isInitial) return
      
      this.debouncedSearch(search)
    },
    debouncedSearch: debounce(async function (search) {
      if (!this.isOpen) return
      if (this.isLoading) return

      this.resetData(search)

      await this.$nextTick()

      await this.getData({
        limit: this.limit,
        search: this.search !== '' ? this.search : undefined,
        page: 1,
      }, 'initial')
    }, 500),
    handleInput(selected) {
      this.localValue = selected.value
      let multiselect = this.$refs?.[`multiselect-${this.attrs.key}`]
      multiselect.updateSearch(this.search)
      this.$emit('update:modelValue', selected)
    },
    clearSearch() {
      this.resetData('')
      let multiselect = this.$refs?.[`multiselect-${this.attrs.key}`]
      
      if (!this.isOpen) {
        this.isInitial = true
        this.isFromClear = true
        multiselect.$el.focus()
      }

      multiselect.updateSearch(this.search)
    },
    resetData(search) {
      this.search = search
      this.isPagination = true
      this.dataApi = []
      this.page = 1
      this.totalCount = null
    }
  }
}
</script>

<style lang="scss" scoped>
.picker-input {
  width: 100%;

  &__input-wrapper {
    display: flex;
  }

  &__clear-icon {
    border: none;
    background-color: transparent;
    padding: 0px;
    margin: 0px;
    cursor: pointer;
    display: flex;
    justify-content: center;
    align-items: center;

    position: absolute;
    right: 10px;
    top: 12px;
    font-size: 16px;
  }

  :deep(.multiselect) {
    .caret-wrapper {
      position: absolute;
      right: 1px;
      top: 1px;
      display: flex;
      padding: 0px 8px;
      gap: 8px;
    }

    .mc-fa {
      height: 35px;
      display: flex;
      justify-content: center;
      align-items: center;

      .icon-fill_close-circle::before {
        font-size: 16px;
      }

      .icon-outline_chevron-down::before {
        font-size: 20px;
      }
    }

    .multiselect__tags {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 8px 60px 8px 8px !important;

      i {
        font-size: 16px;

        &:before {
          color: $mep-color-text-200;
        }
      }

      input {
        padding: 0px;
        color: $mep-color-text-200;

        &::placeholder {
          color: $mep-color-text-200;
          font-size: 12px;
        }
      }

      .multiselect__placeholder {
        height: 20px;
        .placeholder-wrapper {
          height: inherit;
        }

        .__placeholder {
          color: $mep-color-text-200;
          font-size: 12px;
          height: inherit;
          margin-top: 3px;
        }
      }
    }

    .multiselect__content {
      max-width: 300px;
    }

    .multiselect__element:not([role='option']) .multiselect__option--disabled {
      background-color: #fafcff !important;
      color: $mep-color-text-400 !important;
      border-top: 1px solid $mep-primary-100;
      border-bottom: 1px solid $mep-primary-100;
    }

    .multiselect__element {
      .__option-group {
        display: flex;
        gap: 5px;

        .mc-icon {
          cursor: pointer;
          pointer-events: bounding-box;
        }
      }

      .__option-list {
        display: flex;
        overflow-wrap: anywhere;
        gap: 8px;

        .__option-content {
          text-wrap: wrap;
        }
      }
    }

    &.multiselect--active {
      .multiselect__tags {
        padding-right: 30px;
        border-radius: 5px 5px 0 0;
        border: none;
        outline: 2px solid #d6e0ff;
        background-color: $gray;
      }
    }
  }

  .label {
    color: $mep-color-text-300;
  }
}

.validation {
  font-style: italic;
  color: $mep-color-text-300;
  font-size: 12px;
  
  strong {
    color: $red-400;
    margin-right: 5px;
  }
}

.popover-text-wrapper {
  font-family: $font-family-sans-serif;
  font-size: 12px;
  background-color: $mep-color-text-400 !important;
  color: $white !important;
  max-width: 200px;
  min-width: fit-content;
  padding: 6px 12px;
}
</style>